<!-- HxTabbedView Template

Component provides a tabbed view layout with configurable orientation.
Uses content projection with HxTabPanelDirective for tab content.

Vertical: Tabs on left, divider, content on right
Horizontal: Tabs on top, divider, content on bottom

Space allocation controlled via --tabs-ratio CSS variable.
-->

<div class="hx-tabbed-view__container">
  <!-- Tabs List Area -->
  <div
    class="hx-tabbed-view__tabs-list"
    role="tablist"
    [attr.aria-label]="ariaLabel()"
    [attr.aria-orientation]="orientation()">
    @for (tab of enhancedTabs(); track tab.key) {
      <button
        class="hx-tabbed-view__tab"
        role="tab"
        type="button"
        [attr.data-status]="tab.status"
        [attr.aria-controls]="getPanelId(tab.key)"
        [attr.aria-label]="tab.ariaLabel || tab.label"
        [attr.aria-selected]="isTabActive(tab.key)"
        [attr.id]="getTabId(tab.key)"
        [attr.tabindex]="isTabActive(tab.key) ? 0 : -1"
        [class.hx-tabbed-view__tab--active]="isTabActive(tab.key)"
        [class.hx-tabbed-view__tab--disabled]="tab.disabled"
        [class.hx-tabbed-view__tab--invalid]="tab.status === 'invalid'"
        [class.hx-tabbed-view__tab--valid]="tab.status === 'valid'"
        [disabled]="tab.disabled"
        (click)="handleTabClick(tab)"
        (focus)="handleTabFocus(tab)"
        (keydown)="handleTabKeydown($event, tab)">
        @if (tab.icon) {
          <hx-icon
            aria-hidden="true"
            class="hx-tabbed-view__tab-icon"
            [svgIcon]="tab.icon"
            (click)="handleTabIconClick($event, tab)" />
        }
        <span class="hx-tabbed-view__tab-label">{{ tab.label }}</span>
        @if (orientation() === 'horizontal') {
          <hx-icon
            aria-hidden="true"
            class="hx-tabbed-view__tab-chevron"
            [svgIcon]="isTabActive(tab.key) ? 'chevron-up' : 'chevron-down'" />
        } @else {
          <hx-icon
            aria-hidden="true"
            class="hx-tabbed-view__tab-chevron"
            [svgIcon]="isTabActive(tab.key) ? 'chevron-left' : 'chevron-right'" />
        }
      </button>
    }
  </div>

  <!-- Divider -->
  @if (showDivider()) {
    <hx-divider class="hx-tabbed-view__divider" [vertical]="isDividerVertical()" />
  }

  <!-- Content Panel Area - All tabs rendered, inactive ones hidden with CSS -->
  <div class="hx-tabbed-view__panel-container">
    @for (panel of tabPanels(); track panel.hxTabPanel()) {
      <div
        class="hx-tabbed-view__panel"
        role="tabpanel"
        tabindex="0"
        [attr.data-tab-panel-key]="panel.hxTabPanel()"
        [attr.aria-labelledby]="getTabId(panel.hxTabPanel())"
        [attr.id]="getPanelId(panel.hxTabPanel())"
        [attr.aria-hidden]="!isTabActive(panel.hxTabPanel())"
        [class.hx-tabbed-view__panel--hidden]="!isTabActive(panel.hxTabPanel())">
        <ng-container *ngTemplateOutlet="panel.templateRef" />
      </div>
    }
    @if (tabPanels().length === 0) {
      <div class="hx-tabbed-view__panel hx-tabbed-view__panel--empty">
        <p class="hx-tabbed-view__empty-message">No content available for this tab.</p>
      </div>
    }
  </div>
</div>

<!-- Incomplete Tabs Message (matches hx-input error pattern) -->
@if (shouldShowIncompleteBanner()) {
  <span class="hx-tabbed-view__incomplete-message" role="alert">
    {{ incompleteTabsMessage() }}
  </span>
}
